/**
 * Story Editor with Inline Annotations
 *
 * TipTap-based editor that allows inline commenting on WHY stories.
 * Text is read-only; only annotations can be added.
 * Annotations are converted to structured feedback for AI rewrite.
 */

import { Editor } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import { Mark } from '@tiptap/core'
import Placeholder from '@tiptap/extension-placeholder'

// ============================================================================
// Custom Annotation Mark
// ============================================================================

const InlineAnnotation = Mark.create({
  name: 'annotation',

  addAttributes() {
    return {
      annotationId: { default: null },
      commentText: { default: '' },
      createdAt: { default: null },
    }
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', {
      class: 'annotation-highlight',
      'data-annotation-id': HTMLAttributes.annotationId,
    }, 0]
  },

  parseHTML() {
    return [{ tag: 'span.annotation-highlight' }]
  },
})

// ============================================================================
// Story Editor Class
// ============================================================================

class StoryEditorApp {
  constructor() {
    this.editor = null
    this.annotations = []
    this.globalNotes = ''
    this.interviewId = null
    this.versions = []
    this.currentVersionNumber = 1
    this.sourceRewriteId = null
    this.clientName = ''
    this.hasDraft = false
    this.isSubmitting = false
    this.saveTimeout = null
    this.pollInterval = null
    this.isDirty = false
    // Two separate concepts:
    //   isDirty           — current state differs from last save (draft/version).
    //                        Drives the close-confirm popup. Cleared by markClean()
    //                        after every successful save (including autosave).
    //   isPendingVersion  — current state differs from the loaded version's
    //                        stored content (regardless of draft saves). Drives
    //                        the visible "● Modified" indicator. Only cleared
    //                        when the loaded version's text matches the editor.
    this.isPendingVersion = false
    this.loadedContent = ''
    // Baseline snapshot of last-saved/loaded state, used to detect changes
    // since the last commit. Decoupled from loadedContent (the version's
    // stored text used by the no-op save check).
    this._cleanText = ''
    this._cleanAnnotationCount = 0
    this._cleanGlobalNotes = ''
    this._draftEditedContent = null
    this._draftSourceRewriteId = null
  }

  /**
   * Snapshot the current editor state as the clean baseline. After this,
   * the dirty indicator only fires for changes made AFTER this point.
   */
  markClean() {
    this._cleanText = this.extractPlainText()
    this._cleanAnnotationCount = this.annotations.length
    this._cleanGlobalNotes = this.globalNotes || ''
    this.isDirty = false
    // The visible "● Modified" indicator is driven by isPendingVersion, not
    // isDirty — so refresh it from current state vs the loaded version.
    this.recomputePending()
  }

  /**
   * Recompute isDirty by diffing current state against the clean baseline
   * (last save). Drives the close-confirm popup. Does NOT touch the visible
   * indicator — that's recomputePending's job.
   */
  recomputeDirty() {
    if (!this.editor) return
    const textChanged = this.extractPlainText() !== this._cleanText
    const annotationsChanged = this.annotations.length !== this._cleanAnnotationCount
    const notesChanged = (this.globalNotes || '') !== this._cleanGlobalNotes
    this.isDirty = textChanged || annotationsChanged || notesChanged
  }

  /**
   * Recompute isPendingVersion by diffing current state against the loaded
   * version's stored content. Drives the "● Modified" indicator. The
   * indicator stays on as long as the buffer differs from the version,
   * even after autosave — admins want to see when work hasn't yet been
   * promoted to a new version.
   */
  recomputePending() {
    if (!this.editor) return
    const textChanged = this.extractPlainText() !== this.loadedContent
    const hasAnnotations = this.annotations.length > 0
    const hasNotes = (this.globalNotes || '').length > 0
    this.isPendingVersion = textChanged || hasAnnotations || hasNotes
    this.updateModifiedIndicator()
  }

  /**
   * Open the story editor for a given interview.
   */
  async open(interviewId) {
    this.interviewId = interviewId
    this.annotations = []
    this.globalNotes = ''
    this.sourceRewriteId = null
    this._draftEditedContent = null
    this._draftSourceRewriteId = null

    // Fetch versions
    try {
      const resp = await fetch(`/api/admin/reviews/interviews/${interviewId}/editor-versions`)
      if (!resp.ok) throw new Error(`Failed to load versions: ${resp.status}`)
      const data = await resp.json()

      this.versions = data.versions
      this.clientName = data.client_name
      this.hasDraft = data.has_draft

      if (this.versions.length === 0) {
        this.showToast('No story versions found', 'error')
        return
      }

      // Load draft if exists
      if (this.hasDraft) {
        await this.loadDraft()
      }

      this.showEditorModal()

      // Determine the version to display:
      //   1. If a draft exists with a source_rewrite_id, prefer THAT version
      //   2. Else fall back to is_approved
      //   3. Else fall back to the highest-numbered version
      let defaultVersion = null
      if (this._draftSourceRewriteId) {
        defaultVersion = this.versions.find(v => v.rewrite_id === this._draftSourceRewriteId)
      }
      if (!defaultVersion) {
        defaultVersion = this.versions.find(v => v.is_approved)
      }
      if (!defaultVersion) {
        defaultVersion = this.versions[this.versions.length - 1]
      }

      this.loadVersion(defaultVersion)

      // If the draft has an edited buffer, overlay it on top of the loaded version.
      // The draft is already saved server-side, so re-snapshot the clean
      // baseline — admin only becomes dirty by making ADDITIONAL changes.
      if (this._draftEditedContent) {
        const htmlContent = this._draftEditedContent
          .split('\n\n')
          .filter(p => p.trim())
          .map(p => `<p>${this.escapeHtml(p.trim())}</p>`)
          .join('')
        this.editor.commands.setContent(htmlContent, false)
        this.markClean()
      }

    } catch (err) {
      console.error('Failed to open editor:', err)
      this.showToast('Failed to load story editor', 'error')
    }
  }

  /**
   * Render and display the editor modal.
   */
  showEditorModal() {
    // Remove existing modal if any
    const existing = document.getElementById('story-editor-modal')
    if (existing) existing.remove()

    const modal = document.createElement('div')
    modal.id = 'story-editor-modal'
    modal.className = 'story-editor-modal'
    modal.innerHTML = `
      <div class="story-editor-container">
        <div class="story-editor-header">
          <div class="story-editor-title">
            <h2>Story Editor — ${this.escapeHtml(this.clientName)}'s Why Story</h2>
            <div class="story-editor-meta">
              <span class="version-label">Version:</span>
              <select id="version-selector" class="version-selector">
                ${this.versions.map(v => `
                  <option value="${v.version_number}" data-rewrite-id="${v.rewrite_id || ''}">
                    V${v.version_number} (${v.source === 'original' ? 'Original' :
                      v.source === 'editor' ? 'Editor Revision' :
                      v.source === 'import' ? 'Imported' : 'Loom Revision'}${
                      v.is_approved ? ' — Approved' : ''}${
                      v.created_at ? ' — ' + new Date(v.created_at).toLocaleDateString() : ''})
                  </option>
                `).join('')}
              </select>
              <span id="modified-indicator" class="modified-indicator" hidden>● Modified</span>
              <span class="word-count-display" id="editor-word-count">Words: —</span>
            </div>
          </div>
          <button class="story-editor-close" onclick="storyEditor.confirmClose()">&times;</button>
        </div>

        <div class="story-editor-body">
          <div class="story-editor-main">
            <div id="tiptap-editor" class="tiptap-editor"></div>

            <div class="global-notes-section">
              <label for="global-notes">Overall Direction: <span class="global-notes-hint" title="Soft guidance for tone and style when executing annotations. For major structural changes, use the Loom review path instead.">(?)</span></label>
              <textarea id="global-notes" class="global-notes-input"
                placeholder="Soft guidance for tone/style when applying annotations (e.g. 'lean into vulnerability', 'more conversational'). Only annotated passages will be changed."
                rows="3">${this.escapeHtml(this.globalNotes)}</textarea>
            </div>
          </div>

          <div class="story-editor-sidebar">
            <div class="sidebar-header">
              <h3>Comments <span id="annotation-count">(${this.annotations.length})</span></h3>
            </div>
            <div id="annotation-list" class="annotation-list">
              ${this.annotations.length === 0 ? '<p class="no-annotations">Select text to add comments</p>' : ''}
            </div>
            <button class="btn-add-general" onclick="storyEditor.addGeneralNote()">+ Add General Note</button>
          </div>
        </div>

        <div class="story-editor-footer">
          <button class="btn btn-primary" id="btn-save-as-version"
            onclick="storyEditor.saveAsNewVersion()">Save as new version</button>
          <button class="btn btn-primary" id="btn-submit-annotations"
            onclick="storyEditor.submitAnnotations()">Submit for AI revision</button>
          <button class="btn btn-secondary" onclick="storyEditor.saveDraft()">Save Draft</button>
          <button class="btn btn-secondary" onclick="storyEditor.revertChanges()">Revert</button>
          <button class="btn btn-secondary" onclick="storyEditor.triggerImportStory()">Import Story</button>
          <input type="file" id="import-story-file" accept=".txt,.docx,.rtf,.html,.htm,.md" style="display:none;" onchange="storyEditor.handleImportFile(event)" />
          <button class="btn btn-secondary" onclick="storyEditor.confirmClose()">Cancel</button>
          <div id="editor-progress-container" class="editor-progress-container" style="display:none;">
            <div class="progress-bar"><div id="editor-progress-fill" class="progress-bar-fill"></div></div>
            <div id="editor-progress-text" class="progress-text"></div>
          </div>
          <div id="editor-status" class="editor-status"></div>
        </div>
      </div>

      <!-- Floating "+" button (shown on click without selection) -->
      <div id="floating-insert-btn" class="floating-insert-btn" style="display:none;">
        <button class="insertion-trigger-btn" onclick="storyEditor.openInsertionComment()">+ Insert here</button>
      </div>

      <!-- Floating comment input (shown on text selection or insertion) -->
      <div id="floating-comment-input" class="floating-comment-input" style="display:none;">
        <textarea id="comment-input-text" placeholder="Add your comment..." rows="2"></textarea>
        <div class="floating-comment-actions">
          <button class="btn btn-sm btn-primary" onclick="storyEditor.saveComment()">Add</button>
          <button class="btn btn-sm btn-secondary" onclick="storyEditor.cancelComment()">Cancel</button>
        </div>
      </div>
    `
    document.body.appendChild(modal)

    // Initialize TipTap editor
    this.initEditor()

    // Bind version selector
    document.getElementById('version-selector').addEventListener('change', (e) => {
      const targetVersionNumber = parseInt(e.target.value)
      const target = this.versions.find(v => v.version_number === targetVersionNumber)
      if (!target) return

      if (this.isDirty) {
        // Revert the dropdown to the current version visually; the modal
        // will commit the switch only after the user picks an action.
        e.target.value = this.currentVersionNumber
        this.showUnsavedChangesModal({
          afterAction: () => this.loadVersion(target),
        })
      } else {
        this.loadVersion(target)
      }
    })

    // Bind global notes auto-save (also flips dirty since the editor's update
    // event doesn't fire for textarea typing)
    document.getElementById('global-notes').addEventListener('input', (e) => {
      this.globalNotes = e.target.value
      this.recomputeDirty()
      this.recomputePending()
      if (this.isDirty) this.debouncedSaveDraft()
    })
  }

  /**
   * Initialize TipTap with read-only content and annotation support.
   */
  initEditor() {
    this.editor = new Editor({
      element: document.getElementById('tiptap-editor'),
      extensions: [
        StarterKit.configure({
          history: true,
        }),
        InlineAnnotation,
        Placeholder.configure({
          placeholder: 'Story content will appear here...',
        }),
      ],
      // Must be editable: true so setMark/unsetMark work for annotation highlights.
      editable: true,
      content: '<p>Loading...</p>',
      editorProps: {
        // Strip styles, links, images on paste — keep only text + paragraph breaks.
        // Pasting from Google Docs / Word produces clean paragraphs.
        transformPastedHTML: (html) => {
          // Parse the pasted HTML, walk paragraphs, drop everything else.
          const parser = new DOMParser()
          const doc = parser.parseFromString(html, 'text/html')
          const paragraphs = Array.from(doc.body.querySelectorAll('p, div, br'))
            .map(el => el.textContent.trim())
            .filter(t => t.length > 0)
          if (paragraphs.length === 0) {
            // Fallback: treat the whole pasted content as a single paragraph
            const text = doc.body.textContent.trim()
            return text ? `<p>${text.replace(/</g, '&lt;')}</p>` : ''
          }
          return paragraphs.map(p => `<p>${p.replace(/</g, '&lt;')}</p>`).join('')
        },
      },
    })

    // Track dirty state when the user types or annotates. Compare to the
    // clean baseline (set at every save/load) so a freshly-loaded draft
    // doesn't read as dirty until the user makes additional changes.
    this.editor.on('update', () => {
      this.recomputeDirty()
      this.recomputePending()
      // Autosave the draft (debounced) so unexpected closes don't lose work
      if (this.isDirty) this.debouncedSaveDraft()
    })

    // Handle text selection or click for annotation
    document.getElementById('tiptap-editor').addEventListener('mouseup', (e) => {
      // Ignore clicks on the floating buttons themselves
      if (e.target.closest('.floating-insert-btn') || e.target.closest('.floating-comment-input')) return
      this.handleTextSelection(e)
    })
  }

  /**
   * Load a specific version into the editor.
   */
  loadVersion(version) {
    this.currentVersionNumber = version.version_number
    this.sourceRewriteId = version.rewrite_id || null

    // Convert plain text to HTML paragraphs
    const htmlContent = version.story_content
      .split('\n\n')
      .filter(p => p.trim())
      .map(p => `<p>${this.escapeHtml(p.trim())}</p>`)
      .join('')

    // Reset undo history so the user cannot undo into the previous version's text
    if (this.editor.can().clearContent) {
      this.editor.commands.clearContent(false)
    }
    this.editor.commands.setContent(htmlContent, false)
    // commands.setContent with parseOptions.preserveWhitespace would be ideal
    // but the existing call signature works for our paragraph-based HTML.

    // Snapshot the version's stored content for the no-op save check
    // (saveAsNewVersion compares against this). Note: this is the VERSION's
    // text — separate from the dirty-tracking baseline below, which may
    // include draft annotations / overlaid content.
    this.loadedContent = (version.story_content || '')
      .split('\n\n')
      .map(p => p.trim())
      .filter(Boolean)
      .join('\n\n')

    // Update word count
    const wordCount = version.story_content.split(/\s+/).filter(w => w).length
    document.getElementById('editor-word-count').textContent = `Words: ${wordCount}`

    // Update version selector
    document.getElementById('version-selector').value = version.version_number

    // Re-apply existing annotations (this may fire TipTap update events)
    this.reapplyAnnotations()

    // Snapshot the FINAL state as the clean baseline (text + annotations
    // + global notes) AFTER reapplyAnnotations so its update events don't
    // leave us reading dirty when nothing has actually changed.
    this.markClean()
  }


  /**
   * Handle text selection — show floating comment input.
   */
  handleTextSelection(e) {
    // Hide the insert button by default
    document.getElementById('floating-insert-btn').style.display = 'none'

    const selection = window.getSelection()

    if (!selection || selection.isCollapsed || !selection.toString().trim()) {
      // No text selected — show "+" insert button at click position
      this.showInsertButton(e)
      return
    }

    const selectedText = selection.toString().trim()
    if (selectedText.length < 3) return

    // Position floating input near selection, relative to the modal container
    const range = selection.getRangeAt(0)
    const rect = range.getBoundingClientRect()
    const modal = document.getElementById('story-editor-modal')
    const modalRect = modal.getBoundingClientRect()
    const input = document.getElementById('floating-comment-input')

    input.style.display = 'block'
    input.style.top = `${rect.bottom - modalRect.top + modal.scrollTop + 8}px`
    input.style.left = `${Math.max(10, rect.left - modalRect.left)}px`

    // Store selection info
    this._pendingSelection = {
      text: selectedText,
      from: this.editor.state.selection.from,
      to: this.editor.state.selection.to,
    }

    // Focus comment input
    document.getElementById('comment-input-text').value = ''
    document.getElementById('comment-input-text').placeholder = 'Add your comment...'
    document.getElementById('comment-input-text').focus()
  }

  /**
   * Show floating "+" button at click position for insertion annotations.
   */
  showInsertButton(e) {
    if (!e || !e.target) return
    // Only show if click is inside the tiptap editor content
    const tiptapEl = document.querySelector('.tiptap-editor .tiptap')
    if (!tiptapEl || !tiptapEl.contains(e.target)) return

    // Find which paragraph was clicked in/near
    const paragraphs = tiptapEl.querySelectorAll('p')
    let clickedParagraph = null
    let paraIndex = -1
    for (let i = 0; i < paragraphs.length; i++) {
      if (paragraphs[i].contains(e.target) || paragraphs[i] === e.target) {
        clickedParagraph = paragraphs[i]
        paraIndex = i
        break
      }
    }
    if (!clickedParagraph || paraIndex < 0) return

    // Position the "+" button at the bottom-right of the clicked paragraph
    const modal = document.getElementById('story-editor-modal')
    const modalRect = modal.getBoundingClientRect()
    const pRect = clickedParagraph.getBoundingClientRect()
    const insertBtn = document.getElementById('floating-insert-btn')

    insertBtn.style.display = 'block'
    insertBtn.style.top = `${pRect.bottom - modalRect.top + modal.scrollTop + 2}px`
    insertBtn.style.left = `${pRect.left - modalRect.left}px`

    // Store which paragraph to insert after
    this._pendingInsertAfter = paraIndex + 1
  }

  /**
   * Open the comment input for an insertion annotation (triggered by "+" button).
   */
  openInsertionComment() {
    const afterParagraph = this._pendingInsertAfter
    if (!afterParagraph) return

    const insertBtn = document.getElementById('floating-insert-btn')
    const modal = document.getElementById('story-editor-modal')
    const modalRect = modal.getBoundingClientRect()
    const btnRect = insertBtn.getBoundingClientRect()
    const input = document.getElementById('floating-comment-input')

    // Hide the "+" button, show the comment input in its place
    insertBtn.style.display = 'none'
    input.style.display = 'block'
    input.style.top = `${btnRect.top - modalRect.top + modal.scrollTop}px`
    input.style.left = `${btnRect.left - modalRect.left}px`

    this._pendingSelection = {
      text: '',
      after_paragraph: afterParagraph,
      isInsertion: true,
    }

    document.getElementById('comment-input-text').value = ''
    document.getElementById('comment-input-text').placeholder = `What should be added after paragraph ${afterParagraph}?`
    document.getElementById('comment-input-text').focus()
  }

  /**
   * Save a comment from the floating input.
   */
  saveComment() {
    const commentText = document.getElementById('comment-input-text').value.trim()
    if (!commentText || !this._pendingSelection) return

    if (this._pendingSelection.isInsertion) {
      // Insertion annotation — no highlighted text, just a position
      const annotation = {
        id: `ann_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        highlighted_text: '',
        comment: commentText,
        position: { after_paragraph: this._pendingSelection.after_paragraph },
        created_at: new Date().toISOString(),
      }
      this.annotations.push(annotation)

      // Mark the insertion point button as having an annotation
      const btn = document.querySelector(`.insertion-point[data-after-paragraph="${this._pendingSelection.after_paragraph}"]`)
      if (btn) btn.classList.add('has-annotation')

    } else {
      // Highlight annotation — tied to selected text
      const annotation = {
        id: `ann_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        highlighted_text: this._pendingSelection.text,
        comment: commentText,
        position: {
          from: this._pendingSelection.from,
          to: this._pendingSelection.to,
        },
        created_at: new Date().toISOString(),
      }
      this.annotations.push(annotation)

      // Apply highlight mark in editor
      this.editor.chain()
        .focus()
        .setTextSelection({
          from: this._pendingSelection.from,
          to: this._pendingSelection.to,
        })
        .setMark('annotation', {
          annotationId: annotation.id,
          commentText: annotation.comment,
          createdAt: annotation.created_at,
        })
        .run()
    }

    // Update sidebar
    this.renderAnnotationList()
    this.cancelComment()
    this.debouncedSaveDraft()
  }

  /**
   * Cancel the floating comment input.
   */
  cancelComment() {
    document.getElementById('floating-comment-input').style.display = 'none'
    document.getElementById('floating-insert-btn').style.display = 'none'
    document.getElementById('comment-input-text').placeholder = 'Add your comment...'
    this._pendingSelection = null
    this._pendingInsertAfter = null
  }

  /**
   * Add a general note (not tied to specific text).
   */
  addGeneralNote() {
    const comment = prompt('Enter a general comment:')
    if (!comment || !comment.trim()) return

    const annotation = {
      id: `ann_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      highlighted_text: '(General note)',
      comment: comment.trim(),
      position: null,
      created_at: new Date().toISOString(),
    }

    this.annotations.push(annotation)
    this.renderAnnotationList()
    this.debouncedSaveDraft()
  }

  /**
   * Render the annotation list in the sidebar.
   */
  renderAnnotationList() {
    const container = document.getElementById('annotation-list')
    const countEl = document.getElementById('annotation-count')
    countEl.textContent = `(${this.annotations.length})`

    if (this.annotations.length === 0) {
      container.innerHTML = '<p class="no-annotations">Select text to add comments</p>'
      return
    }

    container.innerHTML = this.annotations.map((ann, idx) => {
      const isInsertion = !ann.highlighted_text || ann.highlighted_text === '(Insertion point)'
      const isGeneral = ann.highlighted_text === '(General note)'
      const label = isInsertion
        ? `+ Insert after ¶${ann.position?.after_paragraph || '?'}`
        : isGeneral
        ? '(General note)'
        : `"${this.escapeHtml(this.truncate(ann.highlighted_text, 60))}"`
      const itemClass = isInsertion ? 'annotation-item insertion' : 'annotation-item'
      return `
      <div class="${itemClass}" data-id="${ann.id}"
        onclick="storyEditor.scrollToAnnotation('${ann.id}')">
        <div class="annotation-item-header">
          <span class="annotation-number${isInsertion ? ' insertion-badge' : ''}">${isInsertion ? '+' : idx + 1}</span>
          <button class="annotation-delete" onclick="event.stopPropagation(); storyEditor.deleteAnnotation('${ann.id}')">&times;</button>
        </div>
        <div class="annotation-highlighted">${label}</div>
        <div class="annotation-comment">${this.escapeHtml(ann.comment)}</div>
      </div>`
    }).join('')
  }

  /**
   * Delete an annotation by ID.
   */
  deleteAnnotation(annotationId) {
    const ann = this.annotations.find(a => a.id === annotationId)
    this.annotations = this.annotations.filter(a => a.id !== annotationId)

    if (ann && !ann.highlighted_text && ann.position?.after_paragraph) {
      // Insertion annotation — no editor mark to remove
    } else {
      // Highlight annotation — remove mark from editor
      // Collect full span (annotation may cross multiple text nodes)
      const { doc } = this.editor.state
      let from = null, to = null
      doc.descendants((node, pos) => {
        node.marks.forEach(mark => {
          if (mark.type.name === 'annotation' && mark.attrs.annotationId === annotationId) {
            if (from === null || pos < from) from = pos
            const end = pos + node.nodeSize
            if (to === null || end > to) to = end
          }
        })
      })

      if (from !== null && to !== null) {
        this.editor.chain()
          .focus()
          .setTextSelection({ from, to })
          .unsetMark('annotation')
          .run()
      }
    }

    this.renderAnnotationList()
    this.debouncedSaveDraft()
  }

  /**
   * Scroll to and pulse an annotation's highlighted text.
   */
  scrollToAnnotation(annotationId) {
    // Try highlight mark first (for text annotations)
    const highlight = document.querySelector(`[data-annotation-id="${annotationId}"]`)
    if (highlight) {
      highlight.scrollIntoView({ behavior: 'smooth', block: 'center' })
      highlight.classList.add('annotation-pulse')
      setTimeout(() => highlight.classList.remove('annotation-pulse'), 1500)
      return
    }
    // For insertion annotations, scroll to the target paragraph
    const ann = this.annotations.find(a => a.id === annotationId)
    if (ann && ann.position?.after_paragraph) {
      const editorEl = document.querySelector('.tiptap-editor .tiptap')
      if (editorEl) {
        const paragraphs = editorEl.querySelectorAll('p')
        const targetP = paragraphs[ann.position.after_paragraph - 1]
        if (targetP) {
          targetP.scrollIntoView({ behavior: 'smooth', block: 'center' })
          targetP.style.outline = '2px solid #059669'
          setTimeout(() => { targetP.style.outline = '' }, 1500)
        }
      }
    }
  }

  /**
   * Re-apply annotations after loading a version.
   */
  reapplyAnnotations() {
    // Annotations from a different version may not match — clear them
    // Keep: general notes, insertion annotations, and highlight annotations matching current text
    const content = this.editor.getText()
    this.annotations = this.annotations.filter(ann => {
      if (ann.highlighted_text === '(General note)') return true
      if (!ann.highlighted_text || ann.highlighted_text === '(Insertion point)') return true
      return content.includes(ann.highlighted_text)
    })
    this.renderAnnotationList()
  }

  /**
   * Save the current editor buffer as a new story version with NO AI
   * involvement. Used by the "Save as new version" footer button.
   */
  async saveAsNewVersion() {
    const editedContent = this.extractPlainText()
    if (!editedContent.trim()) {
      this.showToast('Story content cannot be empty', 'error')
      return
    }

    // Reject local no-op (matches backend rejection but avoids the round-trip)
    if (editedContent === this.loadedContent && this.annotations.length === 0) {
      this.showToast('No changes to save', 'error')
      return
    }

    this.setStatus('Saving...')
    try {
      const resp = await fetch(`/api/admin/reviews/interviews/${this.interviewId}/save-version`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          story_content: editedContent,
          source_rewrite_id: this.sourceRewriteId || null,
        }),
      })

      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}))
        throw new Error(errBody.detail || `Save failed: ${resp.status}`)
      }

      const data = await resp.json()
      this.showToast(`Saved as V${data.version_number}`, 'success')

      // Clear local pending state and reload versions
      this.annotations = []
      this.isDirty = false
      this.updateModifiedIndicator()
      this.refreshClientDetailPage()
      this.close()
    } catch (err) {
      console.error('Save as new version failed:', err)
      this.showToast(err.message || 'Save failed', 'error')
      this.setStatus('')
    }
  }

  /**
   * Submit annotations for AI revision.
   */
  async submitAnnotations() {
    if (this.isSubmitting) return
    if (this.annotations.length === 0) {
      this.showToast('Add at least one annotation before submitting', 'error')
      return
    }

    // Drop annotations whose underlying mark has collapsed (e.g. user
    // deleted the highlighted text after annotating). Insertion-type
    // annotations (empty highlighted_text) and general/insertion-point
    // sentinels are kept regardless — they reference paragraphs or are
    // not tied to a text mark at all.
    const validAnnotations = this.annotations.filter(ann => {
      if (!ann.highlighted_text) return true  // insertion annotation
      if (ann.highlighted_text === '(General note)') return true
      if (ann.highlighted_text === '(Insertion point)') return true
      let firstFrom = null
      let lastTo = null
      this.editor.state.doc.descendants((node, pos) => {
        if (!node.isText) return
        const mark = node.marks.find(m =>
          m.type.name === 'annotation' && m.attrs.annotationId === ann.id
        )
        if (!mark) return
        if (firstFrom === null) firstFrom = pos
        lastTo = pos + node.nodeSize
      })
      return firstFrom !== null && lastTo - firstFrom > 0
    })
    const droppedCount = this.annotations.length - validAnnotations.length
    if (droppedCount > 0) {
      console.warn(`Dropping ${droppedCount} zero-width annotation(s) — text was deleted after annotating`)
    }

    if (validAnnotations.length === 0) {
      this.showToast('All annotations refer to deleted text — nothing to submit. Add new annotations or use Save as new version.', 'error')
      return
    }

    this.isSubmitting = true
    const btn = document.getElementById('btn-submit-annotations')
    btn.disabled = true
    btn.textContent = 'Submitting...'
    this.setStatus('Submitting annotations...')

    try {
      const resp = await fetch(`/api/admin/reviews/interviews/${this.interviewId}/editor-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          annotations: validAnnotations,
          global_notes: this.globalNotes || null,
          source_rewrite_id: this.sourceRewriteId || null,
          edited_story_content: this.extractPlainText(),
        }),
      })

      if (!resp.ok) {
        const err = await resp.json()
        throw new Error(err.detail || 'Submission failed')
      }

      const data = await resp.json()
      this.setStatus(`Generating rewrite... (${data.feedback_summary})`)
      this.showToast(`Submitted ${data.annotation_count} annotations. Generating V${this.currentVersionNumber + 1}...`, 'success')

      // Start polling for rewrite completion
      this.pollForRewrite(data.rewrite_id)

    } catch (err) {
      console.error('Submit failed:', err)
      this.showToast(`Submission failed: ${err.message}`, 'error')
      this.setStatus('')
      btn.disabled = false
      btn.textContent = 'Submit for AI revision'
      this.isSubmitting = false
    }
  }

  /**
   * Poll for rewrite completion with progress bar.
   */
  async pollForRewrite(rewriteId) {
    let pollCount = 0
    const maxPolls = 60 // 60 * 2s = 120 second timeout
    const estimatedSeconds = 45

    // Show progress bar
    this.showProgress('Generating AI rewrite...', 0)

    this.pollInterval = setInterval(async () => {
      pollCount++

      // Animate progress bar (ease out toward 90% over estimated time)
      const elapsed = pollCount * 2
      const pct = Math.min(90, Math.round((elapsed / estimatedSeconds) * 80))
      this.showProgress(
        elapsed < 10 ? 'Analyzing annotations...' :
        elapsed < 25 ? 'Rewriting story...' :
        elapsed < 40 ? 'Running quality checks...' :
        'Finalizing...', pct
      )

      if (pollCount > maxPolls) {
        clearInterval(this.pollInterval)
        this.pollInterval = null
        this.isSubmitting = false
        this.hideProgress()
        this.setStatus('Rewrite timed out. Check the review panel for status.')
        this.showToast('Rewrite generation timed out', 'error')
        const btn = document.getElementById('btn-submit-annotations')
        if (btn) { btn.disabled = false; btn.textContent = 'Submit for AI revision' }
        return
      }
      try {
        const resp = await fetch(`/api/admin/reviews/rewrites/${rewriteId}`)
        if (!resp.ok) return

        const data = await resp.json()

        if (data.status === 'generated') {
          clearInterval(this.pollInterval)
          this.pollInterval = null
          this.isSubmitting = false

          // Complete the progress bar
          this.showProgress('Complete!', 100)
          setTimeout(() => this.hideProgress(), 1500)

          this.setStatus(`V${data.version_number} generated! ${data.word_count} words, Gold Standard: ${data.gold_standard_score}/12`)

          const btn = document.getElementById('btn-submit-annotations')
          btn.disabled = false
          btn.textContent = 'Submit for AI revision'

          this.showToast(`Rewrite complete! V${data.version_number} ready for review.`, 'success')

          // Reload versions in the editor
          await this.reloadVersions()

          // Refresh client detail page so new version appears in output list
          this.refreshClientDetailPage()

          // Delete draft since submission succeeded
          this.deleteDraftSilently()

        } else if (data.status === 'rejected') {
          clearInterval(this.pollInterval)
          this.pollInterval = null
          this.isSubmitting = false

          this.hideProgress()
          this.setStatus(`Rewrite failed: ${data.rejection_reason || 'Unknown error'}`)
          this.showToast('Rewrite generation failed', 'error')

          const btn = document.getElementById('btn-submit-annotations')
          btn.disabled = false
          btn.textContent = 'Submit for AI revision'
        }
      } catch (err) {
        console.error('Poll error:', err)
      }
    }, 2000)
  }

  /**
   * Reload versions after a new rewrite is generated.
   */
  async reloadVersions() {
    try {
      const resp = await fetch(`/api/admin/reviews/interviews/${this.interviewId}/editor-versions`)
      if (!resp.ok) return

      const data = await resp.json()
      this.versions = data.versions
      this.hasDraft = data.has_draft

      // Update version selector
      const selector = document.getElementById('version-selector')
      selector.innerHTML = this.versions.map(v => `
        <option value="${v.version_number}" data-rewrite-id="${v.rewrite_id || ''}">
          V${v.version_number} (${v.source === 'original' ? 'Original' :
            v.source === 'editor' ? 'Editor Revision' :
            v.source === 'import' ? 'Imported' : 'Loom Revision'}${
            v.created_at ? ' — ' + new Date(v.created_at).toLocaleDateString() : ''})
        </option>
      `).join('')

      // Load the latest version
      const latest = this.versions[this.versions.length - 1]
      if (latest) {
        this.annotations = [] // Clear annotations for new version
        this.loadVersion(latest)
        selector.value = latest.version_number
      }
    } catch (err) {
      console.error('Failed to reload versions:', err)
    }
  }

  /**
   * Save annotations as a draft.
   */
  async saveDraft() {
    try {
      const resp = await fetch(`/api/admin/reviews/interviews/${this.interviewId}/editor-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          annotations: this.annotations,
          global_notes: this.globalNotes || null,
          source_rewrite_id: this.sourceRewriteId || null,
          edited_content: this.extractPlainText(),
        }),
      })

      if (resp.ok) {
        this.showToast('Draft saved', 'success')
        // The draft is now safely on the server — re-snapshot the baseline
        // so the close-confirm pattern doesn't fire on the next X click.
        this.markClean()
      }
    } catch (err) {
      console.error('Draft save failed:', err)
    }
  }

  /**
   * Debounced auto-save (500ms).
   */
  debouncedSaveDraft() {
    if (this.saveTimeout) clearTimeout(this.saveTimeout)
    this.saveTimeout = setTimeout(() => this.saveDraft(), 500)
  }

  /**
   * Load a saved draft.
   */
  async loadDraft() {
    try {
      const resp = await fetch(`/api/admin/reviews/interviews/${this.interviewId}/editor-draft`)
      if (!resp.ok) {
        // 404 is normal: no draft exists
        return null
      }
      const data = await resp.json()
      this.annotations = data.annotations || []
      this.globalNotes = data.global_notes || ''
      // New fields: edited content + which version was being edited
      this._draftEditedContent = data.edited_content || null
      this._draftSourceRewriteId = data.source_rewrite_id || null
      return data
    } catch (err) {
      console.error('Failed to load draft:', err)
      return null
    }
  }

  /**
   * Delete draft silently after successful submission.
   */
  async deleteDraftSilently() {
    // Delete the server-side draft row
    try {
      await fetch(`/api/admin/reviews/interviews/${this.interviewId}/editor-draft`, {
        method: 'DELETE',
      })
    } catch (err) {
      // Silently ignore
    }
  }

  /**
   * Close the editor modal.
   */
  close() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
      this.pollInterval = null
    }
    if (this.editor) {
      this.editor.destroy()
      this.editor = null
    }
    const modal = document.getElementById('story-editor-modal')
    if (modal) modal.remove()
    this.isSubmitting = false
  }

  /**
   * Close handler that pops a confirmation modal if there are unsaved
   * changes. Used by the X / Cancel buttons in the editor footer.
   */
  confirmClose() {
    if (!this.isDirty) {
      this.close()
      return
    }
    this.showUnsavedChangesModal({ onCancel: null, afterAction: () => this.close() })
  }

  /**
   * Render a "You have unsaved changes" modal with four buttons that map
   * to the same actions as the footer buttons. `afterAction` runs after
   * Save / Save draft / Discard succeed (e.g. to actually close the
   * editor or to perform the version switch the user attempted).
   */
  showUnsavedChangesModal({ afterAction }) {
    // Remove any existing instance
    const existing = document.getElementById('unsaved-changes-modal')
    if (existing) existing.remove()

    const modal = document.createElement('div')
    modal.id = 'unsaved-changes-modal'
    modal.className = 'unsaved-changes-modal'
    modal.innerHTML = `
      <div class="unsaved-changes-content">
        <h3>You have unsaved changes</h3>
        <p>Choose what to do with your pending edits and annotations.</p>
        <div class="unsaved-changes-actions">
          <button class="btn btn-primary" id="uc-save-version">Save as new version</button>
          <button class="btn btn-primary" id="uc-save-draft">Save draft</button>
          <button class="btn btn-secondary" id="uc-discard">Discard</button>
          <button class="btn btn-secondary" id="uc-cancel">Cancel</button>
        </div>
      </div>
    `
    document.body.appendChild(modal)

    const cleanup = () => modal.remove()

    document.getElementById('uc-save-version').onclick = async () => {
      cleanup()
      await this.saveAsNewVersion()
      // saveAsNewVersion already closes the editor on success, so afterAction
      // is only relevant if save failed — in that case isDirty stays true
      // and we leave the editor open.
    }
    document.getElementById('uc-save-draft').onclick = async () => {
      cleanup()
      await this.saveDraft()
      if (afterAction) afterAction()
    }
    document.getElementById('uc-discard').onclick = async () => {
      cleanup()
      await this.discardChanges()
      if (afterAction) afterAction()
    }
    document.getElementById('uc-cancel').onclick = cleanup
  }

  /**
   * Drop pending direct edits + annotations and DELETE the server-side
   * draft. Reloads the currently-selected version's stored content.
   */
  /**
   * Footer "Revert" button: drop pending edits and annotations and reload
   * the loaded version's stored content. Same effect as the popup's
   * Discard, but stays in the editor (no close).
   */
  async revertChanges() {
    if (!this.isPendingVersion) {
      this.showToast('Nothing to revert', 'info')
      return
    }
    await this.discardChanges()
    this.showToast('Reverted to saved version', 'success')
  }

  async discardChanges() {
    try {
      await fetch(`/api/admin/reviews/interviews/${this.interviewId}/editor-draft`, { method: 'DELETE' })
    } catch (err) {
      // Non-fatal — server-side draft cleanup will eventually pick it up
      console.warn('Failed to delete draft on discard:', err)
    }
    this.annotations = []
    this.globalNotes = ''
    this._draftEditedContent = null
    this._draftSourceRewriteId = null
    const currentVersion = this.versions.find(v => v.version_number === this.currentVersionNumber)
    if (currentVersion) {
      this.loadVersion(currentVersion)
    }
    this.isDirty = false
    this.updateModifiedIndicator()
  }

  // ========================================================================
  // Helpers
  // ========================================================================

  setStatus(msg) {
    const el = document.getElementById('editor-status')
    if (el) el.textContent = msg
  }

  showProgress(message, percent) {
    const container = document.getElementById('editor-progress-container')
    const fill = document.getElementById('editor-progress-fill')
    const text = document.getElementById('editor-progress-text')
    if (container) container.style.display = ''
    if (fill) fill.style.width = `${percent}%`
    if (text) text.textContent = `${message} ${percent}%`
    // Hide plain status while progress is showing
    const status = document.getElementById('editor-status')
    if (status) status.style.display = 'none'
  }

  hideProgress() {
    const container = document.getElementById('editor-progress-container')
    if (container) container.style.display = 'none'
    const status = document.getElementById('editor-status')
    if (status) status.style.display = ''
  }

  refreshClientDetailPage() {
    if (window.adminDashboard && window.adminDashboard.currentClientId) {
      window.adminDashboard.showClientDetailPage(window.adminDashboard.currentClientId)
    }
  }

  showToast(message, type = 'info') {
    // Use existing toast system if available, otherwise simple alert-style
    if (window.adminDashboard && typeof window.adminDashboard.showToast === 'function') {
      window.adminDashboard.showToast(message, type)
    } else {
      const toast = document.createElement('div')
      toast.className = `story-editor-toast toast-${type}`
      toast.textContent = message
      document.body.appendChild(toast)
      setTimeout(() => {
        toast.classList.add('toast-fade')
        setTimeout(() => toast.remove(), 300)
      }, 3000)
    }
  }

  /**
   * Open file picker for importing an external story.
   */
  triggerImportStory() {
    const fileInput = document.getElementById('import-story-file')
    if (fileInput) {
      fileInput.value = ''
      fileInput.click()
    }
  }

  /**
   * Handle the selected file and upload it as a new story version.
   */
  async handleImportFile(event) {
    const file = event.target.files[0]
    if (!file) return

    const maxSize = 10 * 1024 * 1024
    if (file.size > maxSize) {
      this.showToast('File too large (max 10MB)', 'error')
      return
    }

    const supportedExts = ['.txt', '.docx', '.rtf', '.html', '.htm', '.md']
    const ext = '.' + file.name.split('.').pop().toLowerCase()
    if (!supportedExts.includes(ext)) {
      this.showToast(`Unsupported format. Supported: ${supportedExts.join(', ')}`, 'error')
      return
    }

    if (!confirm(`Import "${file.name}" as a new story version? This will become the current published version.`)) {
      return
    }

    const formData = new FormData()
    formData.append('file', file)

    const statusEl = document.getElementById('editor-status')
    if (statusEl) statusEl.textContent = 'Importing story...'

    try {
      const resp = await fetch(`/api/admin/reviews/interviews/${this.interviewId}/import-story`, {
        method: 'POST',
        body: formData,
      })

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: 'Import failed' }))
        throw new Error(err.detail || 'Import failed')
      }

      const data = await resp.json()
      this.showToast(`Imported as V${data.version_number} (${data.word_count} words)`, 'success')

      // Reload versions to show the imported story
      await this.reloadVersions()

    } catch (err) {
      console.error('Import failed:', err)
      this.showToast(err.message || 'Failed to import story', 'error')
    } finally {
      if (statusEl) statusEl.textContent = ''
    }
  }

  /**
   * Show or hide the "● Modified" indicator in the editor header based on
   * the pending-version state. Stays visible after autosave (since the
   * work isn't a real version yet); only clears when the buffer matches
   * the loaded version.
   */
  updateModifiedIndicator() {
    const el = document.getElementById('modified-indicator')
    if (!el) return
    if (this.isPendingVersion) {
      el.removeAttribute('hidden')
    } else {
      el.setAttribute('hidden', '')
    }
  }

  /**
   * Extract the editor's content as plain text with paragraphs separated by
   * \n\n — matches the format stored in story_rewrites.story_content.
   */
  extractPlainText() {
    if (!this.editor) return ''
    const paragraphs = []
    this.editor.state.doc.content.forEach(node => {
      paragraphs.push(node.textContent)
    })
    return paragraphs.filter(p => p.trim()).join('\n\n')
  }

  escapeHtml(str) {
    if (!str) return ''
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }

  truncate(str, maxLen) {
    if (!str || str.length <= maxLen) return str
    return str.slice(0, maxLen) + '...'
  }
}

// Export for IIFE bundle
const storyEditor = new StoryEditorApp()

// Make globally accessible
if (typeof window !== 'undefined') {
  window.storyEditor = storyEditor
}

export { StoryEditorApp, storyEditor }
